<script setup lang="ts">
import type { NotivueItem } from 'notivue';
import LinkButton from '../common/LinkButton.vue';

export interface NotivueItemProps {
  icon?: {
    name: string;
    color?: string;
  };
  image?: string;
  actions?: {
    text: string;
    onClick?: () => Promise<void> | void | 'dismiss';
  }[];
}

interface Props {
  item: NotivueItem<NotivueItemProps>;
}

defineProps<Props>();

defineSlots<{
  icon(): void;
  content(): void;
}>();
</script>

<template>
  <div
    :class="{
      'bg-system-success-light': item.type === 'success' || item.type === 'promise-resolve',
      'bg-system-error-light': item.type === 'error' || item.type === 'promise-reject',
      'bg-semantic-background-primary': item.type === 'promise',
      'bg-brand-primary-soft': item.type === 'info',
      'bg-system-warning-light': item.type === 'warning',
      '!bg-white !py-6': item.title,
      '!min-w-[450px]': item.title,
    }"
    class="shadow-toast flex w-full min-w-[350px] flex-nowrap gap-2 rounded-lg px-3 py-2"
  >
    <div class="flex w-1/12 items-start justify-center">
      <slot v-if="!item.props.image && !item.props.icon" name="icon" />
      <div v-if="item.props.image" class="overflow-hidden rounded-full">
        <img class="w-[80px] align-top" :src="item.props.image" />
      </div>
      <template v-if="item.props.icon">
        <IconifyIcon
          :icon="item.props.icon.name"
          :class="`text-${item.props.icon.color} size-8`"
        ></IconifyIcon>
      </template>
    </div>
    <div class="flex w-10/12 flex-col justify-center">
      <slot name="content" />
      <div v-if="item.props.actions" class="mt-2 flex gap-3">
        <LinkButton
          size="sm"
          v-for="action in item.props.actions"
          :key="action.text"
          @click="
            () => {
              if (action.onClick) action.onClick();
              item.clear();
            }
          "
        >
          {{ action.text }}
        </LinkButton>
      </div>
    </div>
    <div class="flex w-1/12 items-start justify-end">
      <button
        class="ml-auto cursor-pointer text-semantic-text-secondary outline-0"
        @click="item.clear"
      >
        <IconifyIcon class="size-5" icon="heroicons:x-mark-16-solid" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.shadow-toast {
  box-shadow: 0px 1px 3px 0px #a6afc366;
}
</style>
